import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import { infoCenterLocales } from 'locale/locale.config';

import { cms } from 'lib/cms';

import { setOpacity, mobileOnly, tabletUp } from 'styles/utils';
import { colors } from 'styles/theme';

import useLoadMoreArticles from 'hooks/useLoadMoreArticles';

import PageLayout from 'components/shared/PageLayout/PageLayout';
import NavbarSpacer from 'components/shared/Navbar/NavbarSpacer';
import Text from 'components/shared/Text/Text';
import Container from 'components/shared/Container/Container';
import Button from 'components/shared/Button/Button';
import PromoListGrid from 'components/shared/PromoList/PromoListGrid';
import Breakpoint from 'components/shared/Breakpoint/Breakpoint';
import InfoCenterNavbar from 'components/InfoCenter/InfoCenterNavbar';
import PostBreadcrumbs from 'components/shared/PostBreadcrums/PostBreadcrums';
import {
  entriesShape,
  menuItemShape,
} from 'components/InfoCenter/InfoCenter.config';
import Flexbox from 'components/shared/Flexbox/Flexbox';
import PageHead from 'components/shared/PageHead/PageHead';
import BottomCta from 'components/shared/BottomCta/BottomCta';

const ARTICLE_LIMIT = 25;
const PAGE_TITLE = 'UTI Q&As';
const PAGE_SLUG = 'uti-questions';

const articleCss = css`
  flex-grow: 1;

  ${mobileOnly} {
    margin-bottom: 70px;
  }

  ${tabletUp} {
    margin-bottom: 140px;
  }
`;

const pageTitleCss = css`
  &.MuiTypography-root {
    border-bottom: 1px solid ${setOpacity(colors.inputBorder, 0.4)};

    ${mobileOnly} {
      margin-bottom: 19px;
      padding-bottom: 17px;
    }

    ${tabletUp} {
      margin-bottom: 42px;
      padding-bottom: 28px;
    }
  }
`;

const loadMoreBtnWrapper = css`
  margin-top: 40px;
`;

function InfoCenterUtiQuestions({ entries, topMenuItems }) {
  const {
    articles,
    fetchingInProgress,
    loadMoreArticles,
    reachedArticleLimit,
  } = useLoadMoreArticles(entries, 'qa', ARTICLE_LIMIT);

  return (
    <>
      <PageHead title="UTI Questions and answers" />
      <PageLayout bottomPadding={0}>
        <NavbarSpacer />
        {/* Navbar */}
        <Breakpoint tablet desktop>
          <InfoCenterNavbar menuItems={topMenuItems} />
        </Breakpoint>

        {/* Breadcrumbs */}
        <Breakpoint mobile>
          <PostBreadcrumbs
            breadcrumbs={[{ title: PAGE_TITLE, slug: PAGE_SLUG }]}
          />
        </Breakpoint>
        <article css={articleCss}>
          <Container>
            {/* Title */}
            <Text
              component="h1"
              fontSize={3.2}
              fontWeight={500}
              serifFont
              css={pageTitleCss}
            >
              {PAGE_TITLE}
            </Text>

            {/* Articles */}
            <PromoListGrid articles={articles} />

            {/* Load more button */}
            {!reachedArticleLimit && (
              <Flexbox justifyContent="center" css={loadMoreBtnWrapper}>
                <Button
                  variant="outlined"
                  onClick={loadMoreArticles}
                  showProgress={fetchingInProgress}
                  disabled={fetchingInProgress}
                >
                  Load more
                </Button>
              </Flexbox>
            )}
          </Container>
        </article>
        <BottomCta />
      </PageLayout>
    </>
  );
}

InfoCenterUtiQuestions.propTypes = {
  entries: PropTypes.shape(entriesShape),
  topMenuItems: PropTypes.arrayOf(PropTypes.shape(menuItemShape)),
};

export async function getStaticPaths() {
  const paths = infoCenterLocales.map(localeCode => ({
    params: { locale: localeCode },
  }));

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps() {
  const entries = await cms.getQuestionArticles(ARTICLE_LIMIT);
  const topMenuItems = await cms.getInfoCenterTopMenu();

  return {
    props: {
      entries,
      topMenuItems,
    },
  };
}

export default InfoCenterUtiQuestions;
